import React from 'react';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { P, A } from '@osmosis/tailwind-ui/components/Text';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';

import Image from 'next/legacy/image';
import elsevier from '../../assets/Elsevierlogo.webp';
import osmosisLogo from '../../assets/osmosis_logo.webp';
import FormattedMessageWrapper from './FormattedMessageWrappers';
import { Button } from '@osmosis/tailwind-ui/components/Button';

export type Props = {
  isPagesRouter?: boolean;
  children?: React.ReactNode;
};

const SEPARATOR = '|';

/**
 * Returns Footer component.
 */
const Footer = ({ isPagesRouter, children }: Props) => {
  const paragraphStyles = `text-wp-sand font-national-2  my-3 !text-xs !font-normal`;
  const separatorStyles = `px-1 ${paragraphStyles}`;
  const linkStyles = `cursor-pointer no-underline ${paragraphStyles}`;

  const linkTextStyles = `my-4 underline ${paragraphStyles}`;

  return (
    <footer className={`bg-wp-graphite ${text0} px-8`}>
      <Box className="mx-auto max-w-[1304px] pb-6 pt-12  lg:pt-20">
        <Flex className="flex-col gap-8 md:mb-8 md:flex-row">
          {/* Osmosis Logo */}
          <Box
            className="flex shrink-0 items-start justify-center py-8 md:justify-start md:py-0"
            style={{ flexBasis: '35%' }}
          >
            <Image
              src={osmosisLogo}
              alt="Osmosis from Elsevier logo"
              width={120}
              height={50}
              objectFit="contain"
            />
          </Box>

          {/* Footer Link Columns */}
          {children}
        </Flex>
      </Box>

      {/* Legal Bar */}
      <Flex className="mx-auto max-w-7xl flex-col justify-between border-t border-neutral-700 px-10 py-8 pt-16 md:flex-row">
        <Box className="mb-6 flex flex shrink-0 justify-center md:mb-0 md:basis-[150px] md:justify-start">
          <A
            className="cursor-pointer no-underline"
            href="https://www.elsevier.com/"
          >
            <Image src={elsevier} alt="Elsevier" width={85} height={94} />
          </A>
        </Box>
        <Box className="flex-1 px-0 md:px-4">
          <Flex>
            <Box>
              <P className={`${paragraphStyles} mt-0`}>
                <FormattedMessageWrapper
                  isPagesRouter={isPagesRouter}
                  id="landing.footer.copyright"
                  defaultMessage="Copyright © {year} Elsevier, its licensors, and contributors. All rights are reserved, including those for text and data mining, AI training, and similar technologies."
                  values={{
                    year: new Date().getFullYear(),
                  }}
                />
              </P>
            </Box>
          </Flex>
          <Flex className="items-center">
            <P className={paragraphStyles}>
              <FormattedMessageWrapper
                isPagesRouter={isPagesRouter}
                id="landing.footer.copyright-terms-privacy-cookies"
                defaultMessage="Cookies are used by this site."
              />
            </P>
            <Button
              variant="transparent"
              className={`${linkTextStyles} ot-sdk-show-settings focus-visible:outline-wp-sand p-0 hover:bg-transparent focus:bg-transparent focus-visible:outline-2 active:bg-transparent`}
              data-testid="ot-sdk-show-settings"
            >
              <FormattedMessageWrapper
                isPagesRouter={isPagesRouter}
                id="landing.footer.copyright-terms-privacy"
                defaultMessage="Cookie Settings"
              />
            </Button>
          </Flex>
          <Flex className="flex-wrap">
            <Box>
              <A
                className={linkStyles}
                href="https://www.elsevier.com/legal/elsevier-website-terms-and-conditions"
                target="_blank"
              >
                <P className={linkTextStyles}>
                  <FormattedMessageWrapper
                    isPagesRouter={isPagesRouter}
                    id="landing.footer.terms"
                    defaultMessage="Terms and Conditions"
                  />
                </P>
              </A>
            </Box>
            <Box>
              <P className={separatorStyles}>{SEPARATOR}</P>
            </Box>
            <Box>
              <A
                className={linkStyles}
                href="https://www.elsevier.com/legal/privacy-policy"
                target="_blank"
              >
                <P className={linkTextStyles}>
                  <FormattedMessageWrapper
                    isPagesRouter={isPagesRouter}
                    id="landing.footer.policy"
                    defaultMessage="Privacy Policy"
                  />
                </P>
              </A>
            </Box>
            <Box>
              <P className={separatorStyles}>{SEPARATOR}</P>
            </Box>
            <Box>
              <A
                className={linkStyles}
                href="https://www.elsevier.com/legal/cookienotice"
                target="_blank"
              >
                <P className={linkTextStyles}>
                  <FormattedMessageWrapper
                    isPagesRouter={isPagesRouter}
                    id="landing.footer.cookie"
                    defaultMessage="Cookie Notice"
                  />
                </P>
              </A>
            </Box>
            <Box>
              <P className={separatorStyles}>{SEPARATOR}</P>
            </Box>
            <Box>
              <A
                className={linkTextStyles}
                href="https://www.elsevier.com/sitemap"
                target="_blank"
              >
                <P className={paragraphStyles}>
                  <FormattedMessageWrapper
                    isPagesRouter={isPagesRouter}
                    id="landing.footer.sitemap"
                    defaultMessage="Sitemap"
                  />
                </P>
              </A>
            </Box>
          </Flex>
          <P className={paragraphStyles}>
            <FormattedMessageWrapper
              isPagesRouter={isPagesRouter}
              id="landing.footer.description"
              defaultMessage="USMLE® is a joint program of the Federation of State Medical Boards
                (FSMB) and the National Board of Medical Examiners (NBME).
                COMLEX-USA® is a registered trademark of The National Board of
                Osteopathic Medical Examiners, Inc. NCLEX-RN® is a registered
                trademark of the National Council of State Boards of Nursing, Inc.
                Test names and other trademarks are the property of the respective
                trademark holders. None of the trademark holders are endorsed by nor
                affiliated with Osmosis or this website."
            />
          </P>
        </Box>
      </Flex>
    </footer>
  );
};

export default Footer;
